import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { listBaskets, getCompetitorData, parseCsvFile, parseAsinListCsv, fetchCompetitorDataFromList } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

function PricingBreakdown() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [csvName, setCsvName] = useState(null);

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try {
            const res = await listBaskets(accountId);
            setBaskets(res.data || []);
        } catch (err) { console.error(err); }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        try {
            const res = await getCompetitorData(selectedBasket.id, accountId);
            setData(res.data || []);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load basket pricing data.');
        }
        finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true); setError('');
        try {
            const parsed = await parseCsvFile(file);
            const parsedRows = parsed.data || [];

            const hasHistoricalPricing = parsedRows.some(r => r.date && r.product_price != null);
            if (hasHistoricalPricing) {
                setData(parsedRows);
                setCsvName(file.name);
                setSelectedBasket({ id: 'csv', name: file.name });
                return;
            }

            const asinList = await parseAsinListCsv(file);
            const asins = asinList.data || [];
            if (!asins.length) throw new Error('CSV has no valid rows for pricing history or ASIN list.');
            if (!accountId) throw new Error('Select an Amazon Ads account before uploading an ASIN list CSV.');

            const fetched = await fetchCompetitorDataFromList(asins, accountId, 'US');
            const rows = fetched.data || [];
            if (!rows.length) {
                throw new Error(fetched?.error || fetched?.message || 'No data returned for ASIN list.');
            }

            setData(rows);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
            if (Array.isArray(fetched?.diagnostics?.failedItems) && fetched.diagnostics.failedItems.length > 0) {
                console.table(fetched.diagnostics.failedItems);
            }
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || 'Error parsing CSV: ' + err.message);
        }
        finally { setLoading(false); }
    };

    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    const asins = [...new Set(data.map(r => r.asin).filter(Boolean))];

    const getYearWeek = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `${year}-W${String(week).padStart(2, '0')}`;
    };

    const getXValue = (r) => {
        if (period === 'day') return r.date;
        return getYearWeek(r.date) || `Wk ${r.week}`;
    };

    // Helper: agrega por semana quedándose con el último registro
    const aggregateByWeek = (asinData) => {
        if (period !== 'week') return asinData;
        const weekMap = {};
        asinData.forEach(r => { weekMap[getXValue(r)] = r; });
        return Object.values(weekMap).sort((a, b) => getXValue(a).localeCompare(getXValue(b)));
    };

    const buildPriceTraces = () => {
        return asins.map(asin => {
            const asinData = data.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = asinData[0]?.brand || 'Unknown';
            const displayData = aggregateByWeek(asinData);

            return {
                x: displayData.map(r => getXValue(r)),
                y: displayData.map(r => r.product_price),
                mode: 'lines+markers',
                line: { shape: 'hv', width: 2 },
                marker: { size: 5 },
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Price: $%{y:.2f}<br>Change: %{customdata[0]:.2f}%<br>Source: %{customdata[1]}<extra></extra>`,
                customdata: displayData.map(r => [r.price_change || 0, r.data_source || 'RapidAPI']),
            };
        });
    };

    const buildPivotTable = () => {
        const keys = [...new Set(data.map(r => getXValue(r)))].sort((a, b) => a.localeCompare(b));
        const rows = keys.map(key => {
            const row = { [period === 'day' ? 'Date' : 'Week']: key };
            asins.forEach(asin => {
                const match = data.find(r => r.asin === asin && getXValue(r) === key);
                const brand = data.find(r => r.asin === asin)?.brand || '';
                if (match) {
                    const priceStr = match.product_price != null ? `$${Number(match.product_price).toFixed(2)}` : '—';
                    const sourceTag = match.data_source === 'JungleScout' || match.data_source === 'Jungle Scout' ? ' (JS)' : ' (RA)';
                    row[`${brand} - ${asin}`] = match.product_price != null ? `${priceStr}${sourceTag}` : '—';
                } else {
                    row[`${brand} - ${asin}`] = '—';
                }
            });
            return row;
        });
        return rows;
    };

    const buildVariationTraces = () => {
        return asins.map(asin => {
            const asinData = data.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = asinData[0]?.brand || 'Unknown';
            const displayData = aggregateByWeek(asinData);

            return {
                x: displayData.map(r => getXValue(r)),
                y: displayData.map(r => r.price_change || 0),
                mode: 'lines+markers',
                line: { shape: 'hv', width: 2 },
                marker: { size: 5 },
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Change: %{y:.2f}%<extra></extra>`,
            };
        });
    };

    const availableDates = [...new Set(data.map(r => r.date).filter(Boolean))].sort((a, b) => new Date(b) - new Date(a));

    useEffect(() => {
        if (availableDates.length > 0 && !selectedDate) {
            setSelectedDate(availableDates[0]);
        }
    }, [availableDates, selectedDate]);

    const matrixBrands = [...new Set(data.map(r => r.brand).filter(Boolean))].sort();

    const matrixRows = useMemo(() => {
        if (!selectedDate) return [];
        const dateData = data.filter(r => r.date === selectedDate);
        const uniqueAsins = [...new Set(dateData.map(r => r.asin))].sort();
        return uniqueAsins.map(asin => {
            const row = { asin };
            matrixBrands.forEach(b => row[b] = '—');
            // All records for this ASIN on the selected date (may include both RapidAPI + JungleScout duplicates)
            const asinRecords = dateData.filter(r => r.asin === asin);
            // Prefer the record that has a computed price_change over one with null
            const record = asinRecords.find(r => r.price_change != null) || asinRecords[0];
            if (record && record.brand) {
                const matchedBrand = matrixBrands.find(b => b.toLowerCase() === record.brand.toLowerCase());
                if (matchedBrand) {
                    row[matchedBrand] = record.price_change != null ? Number(record.price_change).toFixed(4) : '—';
                }
            }
            return row;
        });
    }, [data, selectedDate, matrixBrands]);

    const providerHint = useMemo(() => {
        if (!error) return '';
        const normalized = String(error).toLowerCase();
        if (normalized.includes('not subscribed')) return 'Live provider access is not subscribed. Use a historical tracking CSV or enable RapidAPI subscription for live ASIN-list fetches.';
        if (normalized.includes('too many requests') || normalized.includes('429')) return 'Provider rate limit reached. Retry in a few minutes or reduce ASIN volume per request.';
        if (normalized.includes('external provider failed')) return 'The external provider failed for this request. If this is a basket with cached snapshots, use Overview fallback data.';
        return '';
    }, [error]);

    const pivotData = data.length > 0 ? buildPivotTable() : [];
    const pivotColumns = pivotData.length > 0 ? Object.keys(pivotData[0]) : [];

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Pricing Breakdown</h2>

                {error && <div className="alert alert-danger">{error}</div>}
                {providerHint && <div className="alert alert-warning">{providerHint}</div>}

                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="btn-group btn-group-sm w-100">
                            <button className={`btn ${period === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('week')}>Weekly</button>
                            <button className={`btn ${period === 'day' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('day')}>Daily</button>
                        </div>
                    </div>
                </div>

                {loading && <div className="text-center"><div className="spinner-border" /></div>}

                {!loading && data.length === 0 && csvName && (
                    <div className="alert alert-warning">
                        No pricing data loaded from this CSV. If it is an ASIN list, live fetch can fail when provider access is unavailable.
                    </div>
                )}

                {/* Price evolution chart */}
                {data.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Price Evolution by ASIN</h5>
                            <Plot
                                data={buildPriceTraces()}
                                layout={{
                                    height: Math.max(400, 120 * Math.ceil(asins.length / 3)),
                                    xaxis: { title: { text: period === 'day' ? 'Date' : 'Week' }, ...(period === 'day' ? { type: 'date', tickformat: '%b %d' } : { type: 'category' }) },
                                    yaxis: { title: { text: 'Product Price (USD)' }, tickprefix: '$' },
                                    margin: { l: 60, r: 20, t: 30, b: 55 },
                                    showlegend: true,
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* Breakdown by ASIN */}
                {data.length > 0 && asins.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5 className="mb-3">Breakdown by ASIN</h5>
                            <div className="row">
                                {asins.map(asin => {
                                    const asinData = data.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
                                    const brand = asinData[0]?.brand || 'Unknown';
                                    const displayData = aggregateByWeek(asinData);

                                    return (
                                        <div className="col-md-4 mb-3" key={asin}>
                                            <Plot
                                                data={[{
                                                    x: displayData.map(r => getXValue(r)),
                                                    y: displayData.map(r => r.product_price),
                                                    mode: 'lines+markers',
                                                    line: { shape: 'hv', width: 2 },
                                                    marker: { size: 5 },
                                                    hovertemplate: `Price: $%{y:.2f}<extra></extra>`,
                                                }]}
                                                layout={{
                                                    title: { text: `${brand} - ${asin}`, font: { size: 13, color: '#000' } },
                                                    height: 250,
                                                    xaxis: { title: { text: period === 'day' ? 'Date' : 'Week' }, ...(period === 'day' ? { type: 'date', tickformat: '%b %d' } : { type: 'category' }) },
                                                    yaxis: { title: { text: 'Product Price (USD)' }, tickprefix: '$' },
                                                    margin: { l: 60, r: 15, t: 35, b: 50 },
                                                }}
                                                config={{ responsive: true, displayModeBar: false }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Pivot table */}
                {pivotData.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-header" id="headingHistory">
                            <h5 className="mb-0">
                                <button className="btn btn-link text-decoration-none" data-bs-toggle="collapse" data-bs-target="#collapseHistory">
                                    Price History Table (Click to expand)
                                </button>
                            </h5>
                        </div>
                        <div id="collapseHistory" className="collapse">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped table-hover">
                                        <thead>
                                            <tr>{pivotColumns.map(c => <th key={c}>{c}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {pivotData.map((row, i) => (
                                                <tr key={i}>{pivotColumns.map(c => <td key={c}>{row[c]}</td>)}</tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Price Percentage Variation Matrix */}
                {data.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="mb-0">Price Percentage Variation (by ASIN)</h5>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label">Select Date</label>
                                <select className="form-select" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}>
                                    {availableDates.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>

                            {selectedDate && (() => {
                                const dateData = data.filter(r => r.date === selectedDate);
                                const barItems = dateData
                                    .filter(r => r.price_change != null && r.price_change !== 0)
                                    .map(r => ({ label: `${r.brand || ''} — ${r.asin}`, value: Number(r.price_change) }))
                                    .sort((a, b) => a.value - b.value);

                                return (
                                    <>
                                        {barItems.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="mb-2">Price Percentage Variation (by ASIN) - {period === 'day' ? 'Day' : 'Week'}</h6>
                                                <Plot
                                                    data={[{
                                                        type: 'bar',
                                                        orientation: 'h',
                                                        y: barItems.map(b => b.label),
                                                        x: barItems.map(b => b.value),
                                                        marker: { color: barItems.map(b => b.value >= 0 ? '#00c853' : '#ff1744') },
                                                        hovertemplate: '%{y}<br>Change: %{x:.2f}%<extra></extra>',
                                                    }]}
                                                    layout={{
                                                        height: Math.max(300, barItems.length * 45),
                                                        xaxis: { title: 'Price Change (%)', zeroline: true },
                                                        yaxis: { title: 'ASIN — Brand', automargin: true },
                                                        margin: { l: 180, r: 30, t: 10, b: 50 },
                                                        font: { size: 11 },
                                                    }}
                                                    config={{ responsive: true, displayModeBar: false }}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        )}

                                        <div className="p-2 mb-2 bg-light border rounded small">
                                            <span className="fw-bold">Data for selected date: {selectedDate} 00:00:00</span>
                                        </div>

                                        <div className="card">
                                            <div className="card-header" id="headingVariation">
                                                <button className="btn btn-link text-decoration-none fw-bold" type="button" data-bs-toggle="collapse" data-bs-target="#collapseVariation" aria-expanded="true">
                                                    ▸ Show price % variation table
                                                </button>
                                            </div>
                                            <div id="collapseVariation" className="collapse show">
                                                <div className="card-body p-0">
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-bordered mb-0 text-center" style={{ fontSize: '0.85rem' }}>
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th className="text-start">ASIN</th>
                                                                    {matrixBrands.map(b => <th key={b}>{b}</th>)}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {matrixRows.map(row => (
                                                                    <tr key={row.asin}>
                                                                        <td className="text-start font-monospace fw-bold">{row.asin}</td>
                                                                        {matrixBrands.map(b => {
                                                                            const val = row[b];
                                                                            const num = parseFloat(val);
                                                                            let cls = 'text-muted'; // default: '—' or no data
                                                                            if (val && val !== '—') {
                                                                                if (num < 0) cls = 'text-danger fw-bold';
                                                                                else if (num > 0) cls = 'text-success fw-bold';
                                                                                else cls = 'text-secondary'; // 0.0000 = neutral, no color
                                                                            }
                                                                            return (
                                                                                <td key={b} className={cls}>
                                                                                    {val}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* Price Variation Chart */}
                {data.length > 0 && (
                    <div className="card">
                        <div className="card-body">
                            <h5>Price Variation Chart</h5>
                            <Plot
                                data={buildVariationTraces()}
                                layout={{
                                    height: 400,
                                    xaxis: { title: { text: period === 'day' ? 'Date' : 'Week' }, ...(period === 'day' ? { type: 'date', tickformat: '%b %d' } : { type: 'category' }) },
                                    yaxis: { title: { text: 'Price Change (%)' }, ticksuffix: '%' },
                                    margin: { l: 60, r: 20, t: 30, b: 55 },
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default PricingBreakdown;